// Import the FedEx Smartpost carrier account credentials type for reference
import { findProvinceCode } from "@redotech/locale/provinces";
import { IDefaultCarrierCredentials } from "@redotech/redo-fulfillment-rate-common/create-carrier-account";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import { nonEmptyValidator } from "@redotech/ui/validators";
import { memo } from "react";
import { RedoTextInput } from "../../../../arbiter-components/input/redo-text-input";
import { Text } from "../../../../text";

export const FedexSmartpostCarrierInput = groupInput({
  accountNumber: input<string>({ validator: nonEmptyValidator }),
  hubId: input<string>({ validator: nonEmptyValidator }),
  shippingCity: input<string>({ validator: nonEmptyValidator }),
  shippingCountryCode: input<string>({ validator: nonEmptyValidator }),
  shippingPostalCode: input<string>({ validator: nonEmptyValidator }),
  shippingState: input<string>({ validator: nonEmptyValidator }),
  shippingStreets: input<string>({ validator: nonEmptyValidator }),
  corporateCity: input<string>({ validator: nonEmptyValidator }),
  corporateCompanyName: input<string>({ validator: nonEmptyValidator }),
  corporateCountryCode: input<string>({ validator: nonEmptyValidator }),
  corporateEmailAddress: input<string>({ validator: nonEmptyValidator }),
  corporateFirstName: input<string>({ validator: nonEmptyValidator }),
  corporateJobTitle: input<string>({ validator: nonEmptyValidator }),
  corporateLastName: input<string>({ validator: nonEmptyValidator }),
  corporatePhoneNumber: input<string>({ validator: nonEmptyValidator }),
  corporatePostalCode: input<string>({ validator: nonEmptyValidator }),
  corporateState: input<string>({ validator: nonEmptyValidator }),
  corporateStreets: input<string>({ validator: nonEmptyValidator }),
});

export type FedexSmartpostCarrierValue = InputSchema.Value<
  typeof FedexSmartpostCarrierInput
>;

export type FedexSmartpostCarrierForm = InputSchema.Input<
  typeof FedexSmartpostCarrierInput
>;

export const fedexSmartpostCarrierDefault = (
  defaults?: IDefaultCarrierCredentials,
): FedexSmartpostCarrierValue => ({
  accountNumber: "",
  hubId: "",
  shippingCity: defaults?.address_city || "",
  shippingCountryCode: defaults?.address_country || "",
  shippingPostalCode: defaults?.address_zip || "",
  shippingState: defaults?.address_state
    ? findProvinceCode(defaults.address_state)
    : "",
  shippingStreets: defaults?.address_street || "",
  corporateCity: defaults?.address_city || "",
  corporateCompanyName: defaults?.company_name || "",
  corporateCountryCode: defaults?.address_country || "",
  corporateEmailAddress: defaults?.email || "",
  corporateFirstName: "",
  corporateJobTitle: "",
  corporateLastName: "",
  corporatePhoneNumber: defaults?.phone || "",
  corporatePostalCode: defaults?.address_zip || "",
  corporateState: defaults?.address_state
    ? findProvinceCode(defaults.address_state)
    : "",
  corporateStreets: defaults?.address_street || "",
});

export const CreateFedexSmartpostCarrierForm = memo(
  function CreateFedexSmartpostCarrierForm({
    carrierInput,
  }: {
    carrierInput: FedexSmartpostCarrierForm;
  }) {
    return (
      <>
        <Text fontSize="sm" fontWeight="semibold">
          Account Information
        </Text>
        <RedoTextInput
          label="Account Number"
          placeholder="Your FedEx account number"
          setValue={carrierInput.inputs.accountNumber.setValue}
          value={carrierInput.inputs.accountNumber.value}
        />
        <RedoTextInput
          label="Hub ID"
          placeholder="Your FedEx Smartpost Hub ID"
          setValue={carrierInput.inputs.hubId.setValue}
          value={carrierInput.inputs.hubId.value}
        />
        <Text fontSize="sm" fontWeight="semibold">
          Shipping Address
        </Text>
        <RedoTextInput
          label="Street Address"
          placeholder="123 Main St"
          setValue={carrierInput.inputs.shippingStreets.setValue}
          value={carrierInput.inputs.shippingStreets.value}
        />
        <RedoTextInput
          label="City"
          placeholder="San Francisco"
          setValue={carrierInput.inputs.shippingCity.setValue}
          value={carrierInput.inputs.shippingCity.value}
        />
        <RedoTextInput
          description="Enter 2-letter state code (e.g., CA, NY, TX)"
          label="State"
          placeholder="CA"
          setValue={carrierInput.inputs.shippingState.setValue}
          value={carrierInput.inputs.shippingState.value}
        />
        <RedoTextInput
          label="Postal Code"
          placeholder="94107"
          setValue={carrierInput.inputs.shippingPostalCode.setValue}
          value={carrierInput.inputs.shippingPostalCode.value}
        />
        <RedoTextInput
          label="Country Code"
          placeholder="US"
          setValue={carrierInput.inputs.shippingCountryCode.setValue}
          value={carrierInput.inputs.shippingCountryCode.value}
        />
        <Text fontSize="sm" fontWeight="semibold">
          Corporate Information
        </Text>
        <RedoTextInput
          label="Company Name"
          placeholder="Your Company Inc."
          setValue={carrierInput.inputs.corporateCompanyName.setValue}
          value={carrierInput.inputs.corporateCompanyName.value}
        />
        <RedoTextInput
          label="Street Address"
          placeholder="123 Corporate Blvd"
          setValue={carrierInput.inputs.corporateStreets.setValue}
          value={carrierInput.inputs.corporateStreets.value}
        />
        <RedoTextInput
          label="City"
          placeholder="San Francisco"
          setValue={carrierInput.inputs.corporateCity.setValue}
          value={carrierInput.inputs.corporateCity.value}
        />
        <RedoTextInput
          description="Enter 2-letter state code (e.g., CA, NY, TX)"
          label="State"
          placeholder="CA"
          setValue={carrierInput.inputs.corporateState.setValue}
          value={carrierInput.inputs.corporateState.value}
        />
        <RedoTextInput
          label="Postal Code"
          placeholder="94107"
          setValue={carrierInput.inputs.corporatePostalCode.setValue}
          value={carrierInput.inputs.corporatePostalCode.value}
        />
        <RedoTextInput
          label="Country Code"
          placeholder="US"
          setValue={carrierInput.inputs.corporateCountryCode.setValue}
          value={carrierInput.inputs.corporateCountryCode.value}
        />
        <RedoTextInput
          label="First Name"
          placeholder="John"
          setValue={carrierInput.inputs.corporateFirstName.setValue}
          value={carrierInput.inputs.corporateFirstName.value}
        />
        <RedoTextInput
          label="Last Name"
          placeholder="Doe"
          setValue={carrierInput.inputs.corporateLastName.setValue}
          value={carrierInput.inputs.corporateLastName.value}
        />
        <RedoTextInput
          label="Job Title"
          placeholder="Shipping Manager"
          setValue={carrierInput.inputs.corporateJobTitle.setValue}
          value={carrierInput.inputs.corporateJobTitle.value}
        />
        <RedoTextInput
          label="Phone Number"
          placeholder="555-123-4567"
          setValue={carrierInput.inputs.corporatePhoneNumber.setValue}
          value={carrierInput.inputs.corporatePhoneNumber.value}
        />
        <RedoTextInput
          label="Email Address"
          placeholder="john.doe@example.com"
          setValue={carrierInput.inputs.corporateEmailAddress.setValue}
          value={carrierInput.inputs.corporateEmailAddress.value}
        />
      </>
    );
  },
);
